import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { CoreoService } from './';
import { FilterModel, ObstacleModel, PossibleBarrierModel } from '../models';
import { countBy, keys, } from 'lodash';
import { Http } from '@angular/http';
import { barrierTypes } from '../types';
import { forkJoin } from 'rxjs/observable/forkJoin';

const PROJECT_ID = 24;
const OBSTACLE_SURVEY_ID = 26;
const CLASSIFICATION_TYPE_SURVEY_ID = 27;
const CLASSIFICATION_WIDTH_SURVEY_ID = 28;
const CLASSIFICATION_FISH_SURVEY_ID = 29;
const CLASSIFICATION_SPAM_SURVEY_ID = 50;
const BARRIER_SPOTTING_SURVEY_ID = 30;
const ANONYMOUS_USER_ID = 2018;

const COUNTRY_PATH = 'country_ggzhyw'; // path of the new country attribute, using the map boundaries rather than geolocation
const TYPE_PATH = 'type';

const LEADERBOARD_SIZE = 6;
export type GetRecordsOptions = {
  filter?: FilterModel;
  count?: number;
}

@Injectable()
export class ApiService {
  constructor(private coreo: CoreoService, private http: Http) { }

  private filterToQueryString(filter?: FilterModel): string {
    if (!filter) {
      return '';
    }
    let results: string[] = [];
    if (filter.country) {
      results.push(`${COUNTRY_PATH}: "${filter.country}"`)
    }
    if (filter.types) {
      const includedTypes = Object.keys(filter.types).filter(t => filter.types[t]);
      results.push(`${TYPE_PATH}: {in: [${includedTypes.map(t => `"${t}"`).join(',')}]}`)
    }
    return `data: {${results.join(',')}}`;
  }

  getObstacles(options: GetRecordsOptions): Observable<ObstacleModel[]> {
    return this.getRecordsBySurvey(OBSTACLE_SURVEY_ID, options)
      .map(data => data
        .map(item => {
          let { id, lat, lng, state, user } = item;
          let { created_at, type, photo, height } = item.data;
          let country = item.data[COUNTRY_PATH];
          return {
            id,
            state,
            lat,
            lng,
            type,
            createdAt: created_at,
            photo,
            height,
            country,
            user
          };
        })
        .filter(item => item.lat !== null && item.lng !== null)
      );
  }

  getPossibleBarriers(): Observable<PossibleBarrierModel[]> {
    return this.getRecordsBySurvey(BARRIER_SPOTTING_SURVEY_ID)
      .map(data => data
        .map(item => {
          let { id, lat, lng } = item;
          return {
            id,
            lat,
            lng
          };
        })
        .filter(item => item.lat !== null && item.lng !== null)
      );
  }

  getRecordsBySurvey(surveyId: number, options?: GetRecordsOptions) {
    const { filter, count } = options;
    const filterString = this.filterToQueryString(filter);
    return this.coreo.query(`{
            project(id: ${PROJECT_ID}) {
                records(
                    where: {
                        surveyId: ${surveyId},
                        state: {
                            ne: 1
                        }
                        ${filterString ? `,${filterString}` : ``}
                    },
                    order: "reverse:createdAt"
                    ${count ? `,limit: ${count}` : ``}
                ) {
                    id,
                    lat,
                    lng,
                    state,
                    data,
                    user {
                        id,
                        displayName
                    }
                }
            }
        }`).map(data => data.project.records);
  }

  getRecordById(id: number) {
    return this.coreo.query(`{
          project(id: ${PROJECT_ID}) {
              records(
                  where: {
                      id: ${id}
                  }
              ) {
                  id,
                  lat,
                  lng,
                  state,
                  data
              }
          }
      }`).map(data => data.project.records[0]);
  }

  getCollectionItemGeometry(collectionId: number, key: string) {
    return this.coreo.query(`{
          project(id: ${PROJECT_ID}) {
              collections(where: {id: ${collectionId}}) {
                items (where: {key: "${key}"}){
                  geometry {
                    type
                    coordinates
                  }
                }
              }
          }
      }`).map(data => data.project.collections[0].items[0].geometry);
  }

  getLeaderboard(filter?: FilterModel) {
    const filterString = this.filterToQueryString(filter);
    return this.coreo.query(`{
      project(id: ${PROJECT_ID}) {
          leaderboard(
              limit: ${LEADERBOARD_SIZE},
              where: {
                  surveyId: ${OBSTACLE_SURVEY_ID},
                  userId: { not: ${ANONYMOUS_USER_ID}}
                  state: {
                      ne: 1
                  }
                  ${filterString ? `,${filterString}` : ``}
              }
          ) {
              user {
                  id,
                  displayName,
                  username
              },
              recordCount,

          }
      }
  }`)
      .map(data => data.project.leaderboard.map(entry => ({
        verifiedRecords: entry.recordCount,
        user: entry.user,
        position: entry.position,
      })))
  }

  getCountryLeaderboards() {
    const leaderboardObservables = [];
    for (const type of barrierTypes) {
      const url = `https://adorable-dasik-5a9f87.netlify.app/${type.slug}.json`;
      leaderboardObservables.push(this.http.get(url).map(response => response.json()));
    }

    return forkJoin(leaderboardObservables).map(leaderboardPerType => {
      const leaderboardsPerTypeMap = {};
      barrierTypes.forEach((type, idx) => {
        leaderboardsPerTypeMap[type.slug] = leaderboardPerType[idx];
      })
      return leaderboardsPerTypeMap;
    })
  }

  getClassifications(id: number, surveyId: number) {
    return this.coreo.query(`{
            records(
                where: {
                    projectId: ${PROJECT_ID},
                    parentId: ${id},
                    surveyId: ${surveyId}
                }
            ) {
                id,
                data
            }
        }`).map(data => data.records);
  }

  classifyType(id: number, type: string) {
    let body = {
      projectId: PROJECT_ID,
      surveyId: CLASSIFICATION_TYPE_SURVEY_ID,
      parentId: id,
      data: {
        type
      }
    };
    return this.coreo.post('/records', body);
  }

  classifyWidth(id: number, isFullWidth: string) {
    let body = {
      projectId: PROJECT_ID,
      surveyId: CLASSIFICATION_WIDTH_SURVEY_ID,
      parentId: id,
      data: {
        extension: isFullWidth
      }
    };
    return this.coreo.post('/records', body);
  }

  classifyFish(id: number, hasFishPass: string) {
    let body = {
      projectId: PROJECT_ID,
      surveyId: CLASSIFICATION_FISH_SURVEY_ID,
      parentId: id,
      data: {
        fish_pass: hasFishPass
      }
    };
    return this.coreo.post('/records', body);
  }

  classifySpam(id: number) {
    let body = {
      projectId: PROJECT_ID,
      surveyId: CLASSIFICATION_SPAM_SURVEY_ID,
      parentId: id
    };
    return this.coreo.post('/records', body);
  }

  getTypeResults(id: number) {
    return this.getClassifications(id, CLASSIFICATION_TYPE_SURVEY_ID)
      .map(records => this.groupClassifications(records, 'type'));
  }

  getWidthResults(id: number) {
    return this.getClassifications(id, CLASSIFICATION_WIDTH_SURVEY_ID)
      .map(records => this.groupClassifications(records, 'extension'));
  }

  getFishResults(id: number) {
    return this.getClassifications(id, CLASSIFICATION_FISH_SURVEY_ID)
      .map(records => this.groupClassifications(records, 'fish_pass'));
  }

  spotBarrier(lat: number, lng: number, comment: string) {
    let body = {
      projectId: PROJECT_ID,
      surveyId: BARRIER_SPOTTING_SURVEY_ID,
      lat,
      lng,
      data: {
        comment
      }
    };
    return this.coreo.post('/records', body);
  }

  private groupClassifications(records: any[], by: string) {
    let count = countBy(records, (record) => {
      return record.data[by];
    });
    let labels = keys(count);
    let data = labels.map(key => count[key]);

    return {
      data,
      labels
    };
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/core/services/api.service.ts